/**
 * FormFlu Settings Page
 * 
 * Account settings, profile management, MFA settings, and preferences.
 * 
 * HIPAA COMPLIANCE NOTES:
 * - No PHI displayed on this page
 * - Password changes require current password verification
 * - MFA management is security-critical
 */

import { useState } from 'react';
import { DashboardLayout } from '../../components/dashboard';
import { useAuth, useUserDisplay } from '../../contexts';
import './SettingsPage.css';

type SettingsTab = 'profile' | 'security' | 'notifications' | 'danger';

export function SettingsPage() {
  const { user, logout } = useAuth();
  const userDisplay = useUserDisplay();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Profile form state
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [email] = useState(user?.email || '');
  
  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Notification preferences
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [submissionAlerts, setSubmissionAlerts] = useState(true);
  const [weeklyDigest, setWeeklyDigest] = useState(false);
  const [securityAlerts, setSecurityAlerts] = useState(true);

  const tabs = [
    { id: 'profile' as const, label: 'Profile', icon: '👤' },
    { id: 'security' as const, label: 'Security', icon: '🔒' },
    { id: 'notifications' as const, label: 'Notifications', icon: '🔔' },
    { id: 'danger' as const, label: 'Danger Zone', icon: '⚠️' },
  ];

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    
    // TODO: Implement profile update API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setSuccessMessage('Profile updated successfully!');
    setIsSaving(false);
    
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }
    
    setIsSaving(true);
    
    // TODO: Implement password change API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setSuccessMessage('Password changed successfully!');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setIsSaving(false);
    
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleSaveNotifications = async () => {
    setIsSaving(true);
    
    // TODO: Implement notification preferences API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setSuccessMessage('Notification preferences saved!');
    setIsSaving(false);
    
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleDeleteAccount = () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete your account? This action cannot be undone. All your forms, submissions, and data will be permanently deleted.'
    );
    
    if (confirmed) {
      const doubleConfirmed = window.confirm(
        'This is your final warning. Type "DELETE" in the next prompt to confirm account deletion.'
      );
      
      if (doubleConfirmed) {
        // TODO: Implement account deletion
        alert('Account deletion is not yet implemented. Contact support for assistance.');
      }
    }
  };

  return (
    <DashboardLayout title="Settings">
      <div className="settings-page">
        {/* Success Message */}
        {successMessage && (
          <div className="settings-success">
            <span>✓ {successMessage}</span>
          </div>
        )}

        <div className="settings-container">
          {/* Sidebar Navigation */}
          <aside className="settings-sidebar">
            <nav className="settings-nav">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  className={`settings-nav-item ${activeTab === tab.id ? 'active' : ''} ${tab.id === 'danger' ? 'danger' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className="nav-icon">{tab.icon}</span>
                  <span className="nav-label">{tab.label}</span>
                </button>
              ))}
            </nav>
          </aside>

          {/* Content Area */}
          <main className="settings-content">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <section className="settings-section">
                <div className="section-header">
                  <h2>Profile Information</h2>
                  <p>Update your personal information and email address.</p>
                </div>

                <form onSubmit={handleSaveProfile} className="settings-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="firstName">First Name</label>
                      <input
                        type="text"
                        id="firstName"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="Enter your first name"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="lastName">Last Name</label>
                      <input
                        type="text"
                        id="lastName"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Enter your last name"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      disabled
                      className="input-disabled"
                    />
                    <span className="form-hint">Email cannot be changed. Contact support for assistance.</span>
                  </div>

                  <div className="form-group">
                    <label>Role</label>
                    <div className="role-badge">
                      <span className="badge">{user?.role || 'User'}</span>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary" disabled={isSaving}>
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>

                <div className="profile-avatar-section">
                  <h3>Profile Picture</h3>
                  <div className="avatar-container">
                    <div className="avatar-preview">
                      {userDisplay?.initials || 'U'}
                    </div>
                    <div className="avatar-info">
                      <p>Your avatar is generated from your initials.</p>
                      <p className="text-muted">Custom avatars coming soon!</p>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <section className="settings-section">
                <div className="section-header">
                  <h2>Security Settings</h2>
                  <p>Manage your password and two-factor authentication.</p>
                </div>

                <div className="security-card">
                  <div className="security-card-header">
                    <div className="security-icon">🔐</div>
                    <div>
                      <h3>Change Password</h3>
                      <p>Update your password regularly to keep your account secure.</p>
                    </div>
                  </div>

                  <form onSubmit={handleChangePassword} className="settings-form">
                    <div className="form-group">
                      <label htmlFor="currentPassword">Current Password</label>
                      <input
                        type="password"
                        id="currentPassword"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Enter current password"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="newPassword">New Password</label>
                      <input
                        type="password"
                        id="newPassword"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password"
                        minLength={8}
                        required
                      />
                      <span className="form-hint">Minimum 8 characters</span>
                    </div>

                    <div className="form-group">
                      <label htmlFor="confirmPassword">Confirm New Password</label>
                      <input
                        type="password"
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        required
                      />
                    </div>

                    <div className="form-actions">
                      <button type="submit" className="btn btn-primary" disabled={isSaving}>
                        {isSaving ? 'Updating...' : 'Update Password'}
                      </button>
                    </div>
                  </form>
                </div>

                <div className="security-card">
                  <div className="security-card-header">
                    <div className="security-icon">📱</div>
                    <div>
                      <h3>Two-Factor Authentication (MFA)</h3>
                      <p>Add an extra layer of security to your account.</p>
                    </div>
                  </div>

                  <div className="mfa-status">
                    <div className="mfa-badge enabled">
                      <span className="status-icon">✓</span>
                      <span>MFA Enabled</span>
                    </div>
                    <p>Your account is protected with two-factor authentication.</p>
                  </div>

                  <div className="mfa-actions">
                    <button className="btn btn-secondary">
                      View Recovery Codes
                    </button>
                    <button className="btn btn-secondary">
                      Regenerate QR Code
                    </button>
                  </div>
                </div>

                <div className="security-card">
                  <div className="security-card-header">
                    <div className="security-icon">📋</div>
                    <div>
                      <h3>Active Sessions</h3>
                      <p>Manage your active login sessions.</p>
                    </div>
                  </div>

                  <div className="session-list">
                    <div className="session-item current">
                      <div className="session-info">
                        <span className="session-device">Current Session</span>
                        <span className="session-details">This device • Active now</span>
                      </div>
                      <span className="session-badge">Current</span>
                    </div>
                  </div>

                  <button className="btn btn-secondary" onClick={logout}>
                    Sign Out of All Devices
                  </button>
                </div>
              </section>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <section className="settings-section">
                <div className="section-header">
                  <h2>Notification Preferences</h2>
                  <p>Choose how and when you want to be notified.</p>
                </div>

                <div className="notification-group">
                  <h3>Email Notifications</h3>
                  
                  <div className="notification-item">
                    <div className="notification-info">
                      <span className="notification-title">Email Notifications</span>
                      <span className="notification-desc">Receive important updates via email</span>
                    </div>
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={emailNotifications}
                        onChange={(e) => setEmailNotifications(e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="notification-item">
                    <div className="notification-info">
                      <span className="notification-title">Submission Alerts</span>
                      <span className="notification-desc">Get notified when someone submits a form</span>
                    </div>
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={submissionAlerts}
                        onChange={(e) => setSubmissionAlerts(e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="notification-item">
                    <div className="notification-info">
                      <span className="notification-title">Weekly Digest</span>
                      <span className="notification-desc">Receive a weekly summary of your form activity</span>
                    </div>
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={weeklyDigest}
                        onChange={(e) => setWeeklyDigest(e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="notification-item">
                    <div className="notification-info">
                      <span className="notification-title">Security Alerts</span>
                      <span className="notification-desc">Get notified about security-related events</span>
                    </div>
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={securityAlerts}
                        onChange={(e) => setSecurityAlerts(e.target.checked)}
                        disabled
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <span className="required-badge">Required</span>
                  </div>
                </div>

                <div className="form-actions">
                  <button 
                    className="btn btn-primary" 
                    onClick={handleSaveNotifications}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save Preferences'}
                  </button>
                </div>
              </section>
            )}

            {/* Danger Zone Tab */}
            {activeTab === 'danger' && (
              <section className="settings-section danger-zone">
                <div className="section-header">
                  <h2>Danger Zone</h2>
                  <p>Irreversible and destructive actions. Proceed with caution.</p>
                </div>

                <div className="danger-card">
                  <div className="danger-info">
                    <h3>Export All Data</h3>
                    <p>Download all your forms, submissions, and account data.</p>
                  </div>
                  <button className="btn btn-secondary">
                    Export Data
                  </button>
                </div>

                <div className="danger-card">
                  <div className="danger-info">
                    <h3>Delete All Forms</h3>
                    <p>Permanently delete all your forms and their submissions.</p>
                  </div>
                  <button className="btn btn-danger-outline">
                    Delete All Forms
                  </button>
                </div>

                <div className="danger-card critical">
                  <div className="danger-info">
                    <h3>Delete Account</h3>
                    <p>Permanently delete your account and all associated data. This action cannot be undone.</p>
                  </div>
                  <button className="btn btn-danger" onClick={handleDeleteAccount}>
                    Delete Account
                  </button>
                </div>
              </section>
            )}
          </main>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default SettingsPage;
